import { useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Download,
  Trash2,
  Shield,
  FileText,
  Bell,
  AlertTriangle,
  ExternalLink,
  CheckCircle,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { BrandedSpinner } from '@/components/common/BrandedLoading';
import { permissionsRolesApiService } from '@/services/permissionsRolesApi';
import { useLocation } from 'react-router-dom';

const COOKIE_CONSENT_KEY = 'five_ai_cookie_consent';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.4 },
  }),
};

export default function DataPrivacyPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [exportLoading, setExportLoading] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // --- Export Data ---
  const handleExportData = useCallback(async () => {
    if (!user?.email) return;
    setExportLoading(true);
    setExportError(null);
    setExportSuccess(false);
    try {
      // Reuse the token-wired user-identity service so expired/missing tokens
      // get handled by AuthContext + 401 redirect logic in this page.
      const data = await permissionsRolesApiService.get(
        `/api/v1/users/account/${encodeURIComponent(user.email)}/export`
      );
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `five-ai-data-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setExportSuccess(true);
    } catch (err: unknown) {
      const message =
        (err && typeof err === 'object' && 'message' in err ? String((err as { message?: unknown }).message) : null) ||
        (err instanceof Error ? err.message : null) ||
        'Failed to export data. Please try again.';

      if (message.includes('Authentication required')) {
        navigate(`/login?next=${encodeURIComponent(location.pathname)}`, { replace: true });
        return;
      }

      setExportError(message);
    } finally {
      setExportLoading(false);
    }
  }, [user?.email, navigate, location.pathname]);

  // --- Delete Account ---
  const handleDeleteAccount = useCallback(async () => {
    if (!user?.email || deleteConfirmText !== 'DELETE') return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await permissionsRolesApiService.delete(`/api/v1/users/account/${encodeURIComponent(user.email)}`, {
        body: JSON.stringify({ password: deletePassword }),
        headers: { 'Content-Type': 'application/json' },
      });
      await logout();
      navigate('/', { replace: true });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete account. Please try again.';
      setDeleteError(message);
    } finally {
      setDeleteLoading(false);
    }
  }, [user?.email, deleteConfirmText, deletePassword, logout, navigate]);

  // --- Consent Info ---
  const consentData = (() => {
    try {
      const raw = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (raw) return JSON.parse(raw) as { accepted?: boolean; timestamp?: string; version?: string };
    } catch {
      // ignore
    }
    return null;
  })();

  return (
    <>
      <Header />
      <main className="min-h-screen bg-gradient-to-b from-background to-muted/20">
        <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mb-8"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                <Shield className="h-5 w-5" />
              </div>
              <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Your Data &amp; Privacy</h1>
            </div>
            <p className="text-muted-foreground">
              Manage your personal data, export information, and control your privacy settings.
            </p>
          </motion.div>

          <div className="space-y-6">
            {/* Section 1: Export Your Data */}
            <motion.div custom={0} initial="hidden" animate="visible" variants={fadeUp}>
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Download className="h-5 w-5 text-primary" />
                    <CardTitle className="text-lg">Export Your Data</CardTitle>
                  </div>
                  <CardDescription>
                    Download a copy of all your personal data. This includes your profile information,
                    learning history, preferences, consent records, and activity logs.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <Button
                      onClick={handleExportData}
                      disabled={exportLoading || !user?.email}
                      className="bg-primary hover:bg-primary/90 text-primary-foreground"
                    >
                      {exportLoading ? (
                        <>
                          <BrandedSpinner className="mr-2" />
                          Preparing Export...
                        </>
                      ) : (
                        <>
                          <Download className="mr-2 h-4 w-4" />
                          Request Data Export
                        </>
                      )}
                    </Button>
                    {exportSuccess && (
                      <span className="flex items-center gap-1 text-sm text-green-600">
                        <CheckCircle className="h-4 w-4" />
                        Export downloaded successfully
                      </span>
                    )}
                    {exportError && (
                      <span className="text-sm text-destructive">{exportError}</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Section 2: Delete Your Account */}
            <motion.div custom={1} initial="hidden" animate="visible" variants={fadeUp}>
              <Card className="border-destructive/30">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Trash2 className="h-5 w-5 text-destructive" />
                    <CardTitle className="text-lg text-destructive">Delete Your Account</CardTitle>
                  </div>
                  <CardDescription>
                    Permanently delete your account and all associated data. This action cannot be undone.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-4 mb-4">
                    <div className="flex gap-3">
                      <AlertTriangle className="h-5 w-5 shrink-0 text-destructive mt-0.5" />
                      <div className="text-sm text-muted-foreground space-y-1">
                        <p className="font-medium text-destructive">This will permanently:</p>
                        <ul className="list-disc list-inside space-y-0.5">
                          <li>Delete your profile and personal information</li>
                          <li>Remove all learning history and progress</li>
                          <li>Revoke access to all Five AI services</li>
                          <li>Cancel any active subscriptions</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="destructive"
                    onClick={() => setDeleteDialogOpen(true)}
                    disabled={!user?.email}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete My Account
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-destructive flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" />
                    Confirm Account Deletion
                  </DialogTitle>
                  <DialogDescription>
                    This action is permanent and cannot be reversed. All your data will be permanently deleted.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div>
                    <label className="text-sm font-medium text-foreground" htmlFor="delete-confirm">
                      Type <span className="font-mono font-bold text-destructive">DELETE</span> to confirm
                    </label>
                    <Input
                      id="delete-confirm"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder="Type DELETE"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground" htmlFor="delete-password">
                      Enter your password
                    </label>
                    <Input
                      id="delete-password"
                      type="password"
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      placeholder="Your password"
                      className="mt-1"
                    />
                  </div>
                  {deleteError && (
                    <p className="text-sm text-destructive">{deleteError}</p>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    disabled={deleteConfirmText !== 'DELETE' || !deletePassword || deleteLoading}
                    onClick={handleDeleteAccount}
                  >
                    {deleteLoading ? (
                      <>
                        <BrandedSpinner className="mr-2" />
                        Deleting...
                      </>
                    ) : (
                      'Permanently Delete'
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Section 3: Your Consent History */}
            <motion.div custom={2} initial="hidden" animate="visible" variants={fadeUp}>
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-primary" />
                    <CardTitle className="text-lg">Your Consent History</CardTitle>
                  </div>
                  <CardDescription>
                    Review the consents you have provided and the policies you have agreed to.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {consentData?.accepted && (
                      <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/30 p-3">
                        <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                        <div className="flex-1 text-sm">
                          <p className="font-medium text-foreground">Cookie consent granted</p>
                          <p className="text-muted-foreground">
                            {consentData.timestamp
                              ? new Date(consentData.timestamp).toLocaleDateString('en-GB', {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })
                              : 'Date not recorded'}
                            {consentData.version ? ` (v${consentData.version})` : ''}
                          </p>
                        </div>
                      </div>
                    )}
                    {!consentData?.accepted && (
                      <p className="text-sm text-muted-foreground">No cookie consent recorded yet.</p>
                    )}

                    <div className="pt-2">
                      <p className="text-sm font-medium text-foreground mb-2">Related policies:</p>
                      <div className="flex flex-wrap gap-2">
                        <Link
                          to="/terms"
                          className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80 underline underline-offset-2"
                        >
                          Terms of Service <ExternalLink className="h-3 w-3" />
                        </Link>
                        <Link
                          to="/privacy"
                          className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80 underline underline-offset-2"
                        >
                          Privacy Policy <ExternalLink className="h-3 w-3" />
                        </Link>
                        <Link
                          to="/gdpr"
                          className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80 underline underline-offset-2"
                        >
                          GDPR Policy <ExternalLink className="h-3 w-3" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Section 4: Communication Preferences */}
            <motion.div custom={3} initial="hidden" animate="visible" variants={fadeUp}>
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Bell className="h-5 w-5 text-primary" />
                    <CardTitle className="text-lg">Communication Preferences</CardTitle>
                  </div>
                  <CardDescription>
                    Control what notifications and communications you receive from Five AI.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Link to="/settings/notifications">
                    <Button variant="outline">
                      <Bell className="mr-2 h-4 w-4" />
                      Manage Notification Preferences
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
